import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { writeLastContextId } from "./lastContext";

export interface IdentityMember {
  /** 成員 customer_id（/customer/contexts 回傳；移除成員等操作需用） */
  customer_id?: number;
  customer_name: string;
  /** 成員角色（owner 為負責人，不可被移除） */
  role?: "owner" | "member";
  color?: string;
}

export interface IdentitySupplier {
  supplier_name: string;
  supplier_id: string;
  is_open_now?: boolean;
  phone?: string;
  /**
   * 瓦斯行統一編號（賣方身分）。下單頁在 bootstrap 沒帶 tax_id_number 時的退路來源。
   * /customer/contexts 目前未回傳此欄 → 多數情況為 undefined，呼叫端須容忍缺漏（整段隱藏）。
   */
  tax_id_number?: string | null;
}

/** 安裝位置（customer_delivery）— 對齊 bootstrap available_addresses[].install_locations 與
 *  GET /delivery/address/location/:id 的回傳。deleted 已由後端濾除，故此處不帶。 */
export interface InstallLocation {
  customer_delivery_id: number;
  customer_address_id: number;
  usage_location: string;
  note: string | null;
  floor: number;
  is_elevator: boolean;
}

export interface Address {
  customer_address_id: string | number;
  full_address: string;
  city?: string;
  district?: string;
  address?: string;
  floor?: string;
  has_elevator?: boolean;
  /** bootstrap 預載的此地址既有安裝位置；後端未提供時為 undefined → 前端 fallback lazy load */
  install_locations?: InstallLocation[];
}

export interface AddressInfo {
  addressId: string | number | null | undefined;
  fullAddress: string;
  latitude: number;
  longitude: number;
}

export interface IdentityContext {
  // v0.7 unified context fields (from /customer/contexts)
  context_id: number;
  context_name: string;
  role: "owner" | "member";
  is_payer: boolean;
  member_count: number;
  max_members: number;
  members?: IdentityMember[];
  primary_supplier?: IdentitySupplier;
  // Local-only UI state (not from API):
  label?: string;
  address?: Address[] | string;
  choose_address?: Address | null;
}

interface UserData {
  jwtToken?: string;
  customer_id?: string;
  line_id?: string;
  customer_name?: string;
  name?: string;
  phone?: string;
}

export interface GasData {
  supplier_id: string;
  supplier_name: string;
  /** authentication-validation 回傳的 supplier 子物件（平台設定等）。 */
  supplier?: {
    /** 訂購流程是否顯示發票填寫區；opt-in，未設定（undefined）時前端視為關閉。 */
    show_invoice?: boolean;
  };
  verifiedTicket?: string;
  phoneVerifiedTicket?: string;
  // 使用者在 OTP step 實際驗證過的電話。register payload 內的 customer.mainPhone
  // 必須等於此值，否則 backend 回 401 phone mismatch
  // （見 app-line-login-phone-otp-spec.md §5.3 / §九 invariant #1）。
  verifiedPhone?: string;
  customerInfo?: {
    mainPhone?: string;
    invoiceCarrier?: string;
    taxIdNumber?: string;
    deliveryAddressList?: AddressInfo[];
  };
}

interface UserState {
  //-- 使用者
  userData: UserData | null;
  setUserData: (status: UserData | null) => void;
  //-- 邀請碼，取得瓦斯行資料
  invGasData: GasData | null;
  setInvGasData: (status: GasData | null) => void;
  //-- 身分
  personalID: string | null; //-- 取得個人
  setPersonalID: (status: string | null) => void;
  chooseIdentity: IdentityContext | null; //-- 預設值
  setChooseIdentity: (status: IdentityContext | null) => void;
  //--
  personalAccount: unknown; // Refined from any to unknown
  setPersonalAccount: (status: unknown) => void;
}

export const useUser = create<UserState>()(
  persist(
    (set) => ({
      userData: null,
      setUserData: (status) => set({ userData: status }),
      invGasData: null,
      setInvGasData: (status) => set({ invGasData: status }),
      personalID: null,
      setPersonalID: (status) => set({ personalID: status }),
      chooseIdentity: null,
      // 順手把 context_id 記到 localStorage：下次開機（新 webview／新 sessionStorage）
      // 才能不等 /customer/contexts 就先打 /home。只存 id，不存任何顯示資料。
      setChooseIdentity: (status) => {
        writeLastContextId(status?.context_id ?? null);
        set({ chooseIdentity: status });
      },
      personalAccount: null,
      setPersonalAccount: (status) => set({ personalAccount: status }),
    }),
    {
      name: "userData",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
